import React, { useEffect, useState, useCallback } from "react";
import { Badge, Dropdown, List, Button, Typography, Empty, Divider, message } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { subscribeToPush, ensurePushSubscription } from "../../helper/pushNotifications";

const { Text } = Typography;

const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window;

// Polls unread notifications every 30s — no websockets, mirrors the plan's
// "keep it simple" guidance for Phase 4's notification center.
const linkFor = (type) => {
  if (type?.startsWith("leave")) return "/leave?tab=requests";
  if (type?.startsWith("wfh")) return "/wfh-requests";
  if (type === "payslip_available") return "/pay-run";
  if (type === "auto_logout" || type === "geofence_exit") return "/attendance";
  return null;
};

const NotificationBell = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushGranted, setPushGranted] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );

  const onEnablePush = async () => {
    const ok = await subscribeToPush(request);
    if (ok) {
      message.success("Push notifications enabled");
      setPushGranted(true);
    } else {
      message.error("Could not enable push notifications");
    }
  };

  const fetchNotifications = useCallback(() => {
    request({
      url: `${apiPath.notifications}?limit=10`,
      method: "GET",
      // Runs every 30s in the background (see useEffect below) — must never
      // trigger the app-wide loading overlay, or the whole screen flashes
      // every 30 seconds regardless of what the admin is doing.
      silent: true,
      onSuccess: (res) => {
        setItems(res?.data?.notifications || []);
        setUnreadCount(res?.data?.unreadCount || 0);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Browser permission being "granted" doesn't guarantee a subscription was
  // ever actually created and registered with our backend — see
  // ensurePushSubscription's comment. Fire-and-forget on every mount so a
  // returning admin whose permission was granted outside our own "Enable"
  // flow (or whose subscription row got lost) gets silently re-subscribed.
  useEffect(() => {
    ensurePushSubscription(request).then((ok) => {
      if (ok) setPushGranted(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemClick = (item) => {
    if (!item.isRead) {
      request({ url: apiPath.notificationRead(item._id), method: "PATCH", onSuccess: fetchNotifications });
    }
    const link = linkFor(item.type);
    if (link) navigate(link);
    setOpen(false);
  };

  const onMarkAllRead = () => {
    request({ url: apiPath.notificationReadAll, method: "PATCH", onSuccess: fetchNotifications });
  };

  const dropdownContent = (
    <div style={{ width: 340, background: "#fff", borderRadius: "var(--i-ems-radius)", boxShadow: "var(--i-ems-shadow-lg)", overflow: "hidden", border: "1px solid var(--i-ems-border)" }}>
      <div style={{ padding: "8px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0f0" }}>
        <Text strong>Notifications</Text>
        <Button type="link" size="small" onClick={onMarkAllRead}>
          Mark all read
        </Button>
      </div>
      {/* Capped low enough to reliably fit below the bell on most screens —
          a taller panel risks antd auto-flipping it to open upward instead,
          which collides with the header since the bell sits right at the
          top of the page. */}
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {items.length === 0 ? (
          <Empty description="No notifications" style={{ padding: 24 }} />
        ) : (
          <List
            dataSource={items}
            renderItem={(item) => (
              <List.Item
                onClick={() => onItemClick(item)}
                style={{ padding: "10px 16px", cursor: "pointer", background: item.isRead ? "#fff" : "var(--i-ems-primary-light)", transition: "background-color .15s ease" }}
              >
                <List.Item.Meta
                  title={<Text strong={!item.isRead}>{item.title}</Text>}
                  description={
                    <>
                      <div>{item.message}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(item.created_at).toLocaleString()}
                      </Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
      {pushSupported() && !pushGranted && (
        <>
          <Divider style={{ margin: 0 }} />
          <div style={{ padding: "6px 16px" }}>
            <Button type="link" size="small" onClick={onEnablePush}>
              Enable push notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      visible={open}
      onVisibleChange={setOpen}
      trigger={["click"]}
      overlay={dropdownContent}
      placement="bottomRight"
    >
      <span className="i-ems-bell-trigger">
        <Badge count={unreadCount} size="small" offset={[4, -4]} className={unreadCount > 0 ? "i-ems-badge-pulse" : ""}>
          <BellOutlined style={{ fontSize: 19 }} />
        </Badge>
      </span>
    </Dropdown>
  );
};

export default NotificationBell;
